import React, { useState, useEffect, useMemo } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { Home, MapPin, Key, Navigation } from 'lucide-react';
import { Button } from './ui/button';

// Inner component that can use useMap hook
const MapContent = ({ properties, customers, selectedProperty, setSelectedProperty }) => {
  const map = useMap();
  
  // Fit bounds to show all properties
  useEffect(() => {
    if (!map || !properties.length) return;
    
    const propertiesWithCoords = properties.filter(p => p.latitude && p.longitude);
    if (propertiesWithCoords.length === 0) return;
    
    if (propertiesWithCoords.length === 1) {
      map.setCenter({ lat: propertiesWithCoords[0].latitude, lng: propertiesWithCoords[0].longitude });
      map.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      propertiesWithCoords.forEach(p => {
        bounds.extend({ lat: p.latitude, lng: p.longitude });
      });
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [map, properties]);

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  return (
    <>
      {properties.filter(p => p.latitude && p.longitude).map((property) => (
        <AdvancedMarker
          key={property.id}
          position={{ lat: property.latitude, lng: property.longitude }}
          onClick={() => setSelectedProperty(property)}
        >
          <div className="relative">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white cursor-pointer transition-transform hover:scale-110 ${
              selectedProperty?.id === property.id ? 'bg-primary scale-110' : 'bg-emerald-500'
            }`}>
              <Home className="h-4 w-4 text-white" />
            </div>
          </div>
        </AdvancedMarker>
      ))}
      
      {selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
        <InfoWindow
          position={{ lat: selectedProperty.latitude, lng: selectedProperty.longitude }}
          onCloseClick={() => setSelectedProperty(null)}
          pixelOffset={[0, -35]}
        >
          <div className="p-2 min-w-[200px]">
            <h3 className="font-semibold text-gray-900 mb-1">{selectedProperty.address}</h3>
            <p className="text-sm text-gray-600 mb-2">{getCustomerName(selectedProperty.customer_id)}</p>
            
            {selectedProperty.city && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                <MapPin className="h-3 w-3" />
                {selectedProperty.city}, {selectedProperty.state} {selectedProperty.zip_code}
              </div>
            )}
            
            {selectedProperty.gate_code && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                <Key className="h-3 w-3" />
                Gate: {selectedProperty.gate_code}
              </div>
            )}
            
            {selectedProperty.property_size && (
              <div className="text-xs text-gray-500 mb-2">
                {selectedProperty.property_size.toLocaleString()} {selectedProperty.size_unit}
              </div>
            )}
            
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs"
              onClick={() => {
                const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedProperty.latitude},${selectedProperty.longitude}`;
                window.open(url, '_blank');
              }}
            >
              <Navigation className="h-3 w-3 mr-1" />
              Get Directions
            </Button>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

const PropertyMap = ({ properties, customers }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // Calculate center from properties or use default
  const defaultCenter = useMemo(() => {
    const propertiesWithCoords = properties.filter(p => p.latitude && p.longitude);
    if (propertiesWithCoords.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Center of US
    }
    const avgLat = propertiesWithCoords.reduce((sum, p) => sum + p.latitude, 0) / propertiesWithCoords.length;
    const avgLng = propertiesWithCoords.reduce((sum, p) => sum + p.longitude, 0) / propertiesWithCoords.length;
    return { lat: avgLat, lng: avgLng };
  }, [properties]);

  const propertiesWithCoords = properties.filter(p => p.latitude && p.longitude);

  // If no API key, show placeholder
  if (!apiKey) {
    return (
      <div className="h-[400px] bg-gray-100 rounded-lg flex flex-col items-center justify-center border border-gray-200">
        <MapPin className="h-12 w-12 text-gray-300 mb-4" />
        <p className="text-gray-500 text-center mb-2">Google Maps API Key Required</p>
        <p className="text-sm text-gray-400 text-center max-w-xs">
          Add your Google Maps API key to REACT_APP_GOOGLE_MAPS_API_KEY in the frontend .env file to enable map visualization.
        </p>
        {propertiesWithCoords.length > 0 && (
          <p className="text-xs text-gray-400 mt-2">
            {propertiesWithCoords.length} properties have coordinates
          </p>
        )}
      </div>
    );
  }

  // If no properties have coordinates, show message
  if (propertiesWithCoords.length === 0) {
    return (
      <div className="h-[400px] bg-gray-100 rounded-lg flex flex-col items-center justify-center border border-gray-200">
        <MapPin className="h-12 w-12 text-gray-300 mb-4" />
        <p className="text-gray-500 text-center mb-2">No Properties to Display</p>
        <p className="text-sm text-gray-400 text-center max-w-xs">
          Add latitude and longitude coordinates to your properties to see them on the map.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[400px] rounded-lg overflow-hidden border border-gray-200" data-testid="property-map">
      <APIProvider apiKey={apiKey}>
        <Map
          style={{ width: '100%', height: '100%' }}
          defaultCenter={defaultCenter}
          defaultZoom={12}
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl={true}
          mapTypeControl={true}
          streetViewControl={false}
          fullscreenControl={true}
        >
          <MapContent 
            properties={properties} 
            customers={customers}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
          />
        </Map>
      </APIProvider>
    </div>
  );
};

export default PropertyMap;
